import {DecimalPipe} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {ConfirmationService, MessageService} from 'primeng/api';
import {combineLatest, map} from 'rxjs';
import {environment} from 'src/environments/environment';
import {LocalizedDatePipe} from '../components/LocalizedDatePipe';
import {MiniBookAdminService} from '../service/mini-book-admin.service';
import {Room} from '../shared/Room';
import {Timeslot} from '../shared/Timeslot';
import {TimetableEntry} from '../shared/TimetableEntry';
import { HttpResponse } from '@angular/common/http';

type RelatedTimetableEntry = TimetableEntry & {
  room: Room,
  timeslot: Timeslot,
  roomFilter: string,
  slotFilter: string,
  typeFilter: string,
  titleFilter: string
};

@Component({
    selector: 'edu-udo-itmc-dev-mini-book-timetable-entries',
    templateUrl: './timetable-entries.component.html',
    styleUrls: ['./timetable-entries.component.css'],
    standalone: false
})
export class TimetableEntriesComponent implements OnInit {

  timetableEntriesRelations: RelatedTimetableEntry[] = [];
  clonedTimetableEntry?: TimetableEntry;

  rowTrackBy = (tte: RelatedTimetableEntry) => tte.id;

  constructor(
    private messageService: MessageService,
    private adminService: MiniBookAdminService,
    private confirmationService: ConfirmationService,
    private localizedDatePipe: LocalizedDatePipe,
    private decimalPipe: DecimalPipe,
    private router: Router
  ) { }

  ngOnInit() {
    combineLatest([this.adminService.timetableEntries.getTimetableEntries(), this.adminService.timeslots.getTimeslots(), this.adminService.rooms.getRooms()]).pipe(
      map<[timetableEntry: TimetableEntry[], timeslots: Timeslot[], rooms: Room[]], RelatedTimetableEntry[]>(value => {
        let relatedTimetableEntries: RelatedTimetableEntry[] = [];
        value[0].forEach(timetableEntry => relatedTimetableEntries.push({
          ...timetableEntry.toJson(),
          timeslot: value[1].find(timeslot => timeslot.id === timetableEntry.timeslotId),
          room: value[2].find(room => room.id === timetableEntry.roomId),
          typeFilter: timetableEntry.type
        }));
        return relatedTimetableEntries;
      }),
      map((relatedTimetableEntries: RelatedTimetableEntry[]) => relatedTimetableEntries.map(value => {
        const titleString = value.title ? value.title : '';
        const subtitleString = value.subtitle ? value.subtitle : '';
        value.slotFilter = `${this.localizedDatePipe.transform(value.timeslot.date, 'yyyy-MM-dd')} ${this.decimalPipe.transform(value.timeslot.start.hours, '2.0-0')}:${this.decimalPipe.transform(value.timeslot.start.minutes, '2.0-0')} - ${this.decimalPipe.transform(value.timeslot.end.hours, '2.0-0')}:${this.decimalPipe.transform(value.timeslot.end.minutes, '2.0-0')}`;
        value.roomFilter = [value.room.name, value.room.address, value.room.floor, value.room.room].filter(s => s.trim() != '').join(" ");
        value.titleFilter = `${titleString} ${subtitleString}`
        return value;
      })
      )
    ).subscribe({
      next: value => this.timetableEntriesRelations = value,
      error: err => {
        // TODO: Message ausbessern
        this.messageService.add({summary: 'Something bad happened'});
      }
    });
  }

  onTimetableEntryEditInit(tte: TimetableEntry) {
    this.router.navigate(['timetableEntries', tte.id]);
  }

  onTimetableCopyEntryInit(tte: TimetableEntry) {
    this.router.navigate(['timetableEntries', 0], {queryParams: {cloneId: tte.id}});
  }

  deleteTimetableEntry(timetableEntry: TimetableEntry) {
    this.confirmationService.confirm({
      accept: () => {
        this.adminService.timetableEntries.deleteTimetableEntry(timetableEntry.id).subscribe({
          next: () => this.timetableEntriesRelations = this.timetableEntriesRelations.filter(value => value.id !== timetableEntry.id),
          error: err => {
            this.messageService.add({
              summary: 'Fehler bei der Kommunikation mit dem Backend',
              detail: err.message,
              severity: 'error'
            });
          }
        });
      },
      header: 'Bestätigung',
      message: 'Sind Sie sicher, dass Sie die Veranstaltung und Teilnehmerliste löschen möchten?',
      acceptLabel: 'Löschen',
      acceptIcon: 'pi pi-trash',
      rejectLabel: 'Abbrechen'
    });
  }

  addTimetableEntry() {
    this.onTimetableEntryEditInit(new TimetableEntry());
  }

  showAttendeeList(timetableEntry: TimetableEntry) {
    this.router.navigate(['timetableEntries', timetableEntry.id, 'attendees']);
  }

  deleteAllUsersEntry(timetableEntry: TimetableEntry) {
    // Use Element as helper to escape HTML
    const helper = document.createElement('DIV');
    helper.innerText = timetableEntry.title;
    this.confirmationService.confirm({
      accept: () => this.adminService.timetableEntries.deleteAllUsers(timetableEntry.id).subscribe(),
      header: 'Bestätigung',
      message: `Sind Sie sicher, dass Sie die Interessiertenliste für die Veranstaltung<br/><br />${helper.innerHTML}<br/><br />löschen möchten?`,
      acceptLabel: 'Löschen',
      acceptIcon: 'pi pi-trash',
      rejectLabel: 'Abbrechen'
    });
  }

  downloadAttendeeList(timetableEntry: TimetableEntry){
      this.adminService.timetableEntries.downloadAttendeeList(timetableEntry.id).subscribe({
        next: (response: HttpResponse<Blob>) => {
          let filename = response.headers.get('content-disposition')?.split('"')[1];
          const csvUrl = URL.createObjectURL(response.body!);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = csvUrl;
          a.download = filename ?? 'teilnehmer.csv';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(csvUrl);
        },
        error: () => {
          this.messageService.add({severity: 'error', summary: 'Fehler', detail: 'Teilnehmerliste konnte nicht exportiert werden.'})
        }
      });
  }
}
